// Shared-element enter/exit transition (generalised for the gameweek
// picker → results-modal expand, which reuses this same implementation with a different anchor).
//
// This module holds the *pure* pieces of the transition — geometry, easing, timing constants and
// the small decisions ("is this rect degenerate?", "should we skip the shared element?") — so they
// can be unit-tested without a DOM. The stateful controller that drives requestAnimationFrame and
// coordinates the React render swap lives in `useSharedElementTransition.ts`; the clone element
// that flies lives in `SharedElementOverlay.tsx`.
//
// The flight animates `transform` and `opacity` only — never `font-size` or any layout property.
// The clone is rendered at the *destination* font size and scaled DOWN to match the source at the
// start of the flight, so the final frame (scale 1) is pixel-crisp.

import { prefersReducedMotion } from './scoreCountUp'

// ---- Timings (table — starting values, tuned on device) ----
export const EXPAND_FLIGHT_MS = 320
export const CROSSFADE_MS = 120
// Detail content reveal after the header settles (§7/§8): the member list must not begin
// before flight completion.
export const LIST_ROW_STAGGER_MS = 20
// Only the first N rows stagger; the remainder appear together (§ list row stagger).
export const LIST_STAGGER_ROW_COUNT = 8

export interface TransitionRect {
  left: number
  top: number
  width: number
  height: number
}

/** A rect we must never animate from: zero (or negative) size in either axis means the node isn't
 * laid out (hidden, unmounted, display:none). Animating from it would produce a degenerate flight,
 * so callers fall back to a cross-fade instead. */
export function isDegenerateRect(
  rect: TransitionRect | null | undefined
): boolean {
  return !rect || rect.width <= 0 || rect.height <= 0
}

/** Geometry of a flight, expressed relative to the destination. The clone is anchored (position:
 * fixed) at the destination's top-left and rendered at the destination font size; at progress 1 it
 * sits at identity (scale 1, no translate) exactly over the real destination name. `startScale` and
 * `startDx/startDy` are the transform at progress 0, matching the source. */
export interface FlightGeometry {
  destLeft: number
  destTop: number
  destFontSize: number
  startScale: number
  startDx: number
  startDy: number
}

/** Build the flight geometry, or `null` when the shared element must be skipped (degenerate rect or
 * a non-positive font size). Scale comes from the font-size ratio, not the rect widths — the source
 * name box is a flex/grid cell wider than its text, so its rect width is not the text width. Both
 * names are left-aligned (`transform-origin: left top`), so horizontal handoff keys off `left`. */
export function computeFlightGeometry(
  source: TransitionRect,
  dest: TransitionRect,
  sourceFontSize: number,
  destFontSize: number
): FlightGeometry | null {
  if (isDegenerateRect(source) || isDegenerateRect(dest)) return null
  if (!(sourceFontSize > 0) || !(destFontSize > 0)) return null
  return {
    destLeft: dest.left,
    destTop: dest.top,
    destFontSize,
    startScale: sourceFontSize / destFontSize,
    startDx: source.left - dest.left,
    startDy: source.top - dest.top,
  }
}

export interface FlightFrame {
  translateX: number
  translateY: number
  scale: number
}

/** The clone's transform at an eased progress value. progress 0 → matches the source; progress 1 →
 * identity over the destination. Interpolation is linear in the *eased* value: the easing has
 * already been applied by the caller. */
export function flightFrameAt(
  geometry: FlightGeometry,
  easedProgress: number
): FlightFrame {
  const remaining = 1 - easedProgress
  return {
    translateX: geometry.startDx * remaining,
    translateY: geometry.startDy * remaining,
    scale: geometry.startScale + (1 - geometry.startScale) * easedProgress,
  }
}

export function frameToTransform(frame: FlightFrame): string {
  return `translate(${frame.translateX}px, ${frame.translateY}px) scale(${frame.scale})`
}

/** A cubic-bezier timing function `(x1,y1,x2,y2)` as a JS `progress → eased` mapping, matching the
 * CSS `cubic-bezier()` the design tokens use. Solves x for a given progress via Newton-Raphson with
 * a bisection fallback, then evaluates y. Clamped to [0,1] at the ends. */
export function cubicBezier(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): (progress: number) => number {
  const cx = 3 * x1
  const bx = 3 * (x2 - x1) - cx
  const ax = 1 - cx - bx
  const cy = 3 * y1
  const by = 3 * (y2 - y1) - cy
  const ay = 1 - cy - by

  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t
  const sampleDerivX = (t: number) => (3 * ax * t + 2 * bx) * t + cx

  const solveT = (x: number) => {
    let t = x
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x
      if (Math.abs(err) < 1e-6) return t
      const d = sampleDerivX(t)
      if (Math.abs(d) < 1e-6) break
      t -= err / d
    }
    let lo = 0
    let hi = 1
    t = x
    for (let i = 0; i < 20; i++) {
      const err = sampleX(t) - x
      if (Math.abs(err) < 1e-6) break
      if (err > 0) hi = t
      else lo = t
      t = (lo + hi) / 2
    }
    return t
  }

  return (progress: number) => {
    if (progress <= 0) return 0
    if (progress >= 1) return 1
    return sampleY(solveT(progress))
  }
}

// Flight easing (ticket table). Expand: energetic settle. Collapse: accelerate away.
export const EXPAND_EASING = cubicBezier(0.2, 0, 0, 1)
export const COLLAPSE_EASING = cubicBezier(0.4, 0, 1, 1)

/** Decide whether a tap should use the shared-element flight or fall back to a cross-fade. Reduced
 * motion and any degenerate rect both force the cross-fade. The haptic is fired regardless by the
 * caller — it is not part of this decision. */
export function shouldUseSharedElement(
  source: TransitionRect | null,
  reduced = prefersReducedMotion()
): boolean {
  return !reduced && !isDegenerateRect(source)
}

/** Light-impact haptic on touch-down (ticket §expand). Responsive, not confirmatory — fired on
 * press, not release, and still fired on the reduced-motion path. Guarded for environments without
 * the Vibration API (desktop, SSR, tests).
 *
 * `durationMs` lets the pull gesture fire a lighter arm tick and a heavier release
 * one; two taps of the same weight within ~100ms read as a stutter, not as two events. No iOS
 * browser implements the Vibration API, so all of this is an Android-only enhancement. */
export function fireLightHaptic(durationMs = 10): void {
  const nav = (
    globalThis as {
      navigator?: { vibrate?: (pattern: number | number[]) => boolean }
    }
  ).navigator
  if (nav && typeof nav.vibrate === 'function') {
    try {
      nav.vibrate(durationMs)
    } catch {
      // A denied/failed vibration must never break navigation.
    }
  }
}

// Minimal structural element type so this module compiles without the DOM lib (its unit test runs
// under a DOM-less tsconfig). Real callers pass HTMLElement, which satisfies these shapes.
interface MeasurableElement {
  getBoundingClientRect(): {
    left: number
    top: number
    width: number
    height: number
  }
}

/** Read the computed font size (px) of an element, or 0 if it can't be resolved. */
export function readFontSizePx(el: MeasurableElement): number {
  const getComputedStyle = (
    globalThis as { getComputedStyle?: (el: unknown) => { fontSize: string } }
  ).getComputedStyle
  if (typeof getComputedStyle !== 'function') return 0
  const value = parseFloat(getComputedStyle(el).fontSize)
  return Number.isFinite(value) ? value : 0
}

/** Snapshot an element's viewport rect as a plain, serialisable `TransitionRect`. */
export function rectOf(el: MeasurableElement): TransitionRect {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, width: r.width, height: r.height }
}

/** Per-row reveal delay for the detail member list (§ list row stagger). Rows past the stagger
 * window all share the final row's delay, so they appear together rather than trailing off. */
export function listRowRevealDelayMs(rowIndex: number): number {
  const capped = Math.min(rowIndex, LIST_STAGGER_ROW_COUNT - 1)
  return capped * LIST_ROW_STAGGER_MS
}
